import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { SupportInquiryReason } from "@autorepair/contracts";
import { supportApi, ApiError } from "../lib/api";
import { useAuth } from "../lib/useAuth";
import { ModernButton, ModernCard, ModernErrorBanner, ModernField, ModernInput, ModernSelect, ModernSuccessBanner, ModernTextarea } from "../components/ui-modern";

const REASON_OPTIONS: { value: SupportInquiryReason; label: string }[] = [
  { value: "technical", label: "Technical Support" },
  { value: "billing", label: "Billing" },
  { value: "sales_access", label: "Sales / Access" },
  { value: "other", label: "Other" },
];

/**
 * The in-app "Contact support" page — owner-only (see AppShellModern.tsx's
 * nav gating). Posts to /support/inquiries/app, which derives name/email
 * from the session itself (never sent from here), tags the inquiry
 * `source: 'app'`, and attaches the current tenant — see
 * apps/api/src/support/support.service.ts.
 */
export function SupportPageModern() {
  const { session } = useAuth();
  const [reason, setReason] = useState<SupportInquiryReason>("technical");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submitMutation = useMutation({
    mutationFn: () => supportApi.submitFromApp({ reason, subject, message }),
    onSuccess: () => {
      setSubject("");
      setMessage("");
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not send your message — please try again."),
  });

  return (
    <div className="mx-auto max-w-xl">
      <h1 className="m-0 mb-1 text-[27px] font-bold text-[#071a33]">Support</h1>
      <p className="mt-0 mb-4 text-[14px] text-[#5e6b7d]">
        Use this form for questions about using Bay2Bill, something that isn't working as expected, or billing and account
        changes. If you're reporting a problem, tell us what you were doing and which invoice, estimate or work order it
        involved, if any. We'll reply by email to <strong className="font-bold text-[#071a33]">{session?.user.email}</strong>.
      </p>

      <ModernCard>
        {error && <ModernErrorBanner message={error} />}
        {submitMutation.isSuccess && <ModernSuccessBanner message="Sent — we'll reply by email." />}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitMutation.mutate();
          }}
        >
          <ModernField label="Reason" htmlFor="reason" required>
            <ModernSelect id="reason" value={reason} onChange={(e) => setReason(e.target.value as SupportInquiryReason)}>
              {REASON_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </ModernSelect>
          </ModernField>
          <ModernField label="Subject" htmlFor="subject" required>
            <ModernInput id="subject" required autoFocus value={subject} onChange={(e) => setSubject(e.target.value)} />
          </ModernField>
          <ModernField label="Message" htmlFor="message" required>
            <ModernTextarea id="message" required rows={6} value={message} onChange={(e) => setMessage(e.target.value)} />
          </ModernField>
          <ModernButton type="submit" disabled={submitMutation.isPending || !subject.trim() || !message.trim()}>
            {submitMutation.isPending ? "Sending…" : "Send message"}
          </ModernButton>
        </form>
      </ModernCard>
    </div>
  );
}
